import { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, Package, MapPin, Clock, ArrowRight } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import PickupTokenDisplay from '@/components/PickupTokenDisplay';
import { useCart } from '@/hooks/useCartSync';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const { clearCart } = useCart();
  const [verifying, setVerifying] = useState(true);
  const [verified, setVerified] = useState(false);
  const [orderIds, setOrderIds] = useState<string[]>([]);
  const expiredToastRef = useRef<{ dismiss: () => void } | null>(null);
  
  const sessionId = searchParams.get('session_id');
  const rawProvider = (searchParams.get('provider') || 'stripe').toLowerCase();
  const normalizedProvider = rawProvider.replace(/[^a-z]/g, '');
  const provider = normalizedProvider.includes('maishapay')
    ? 'maishapay'
    : normalizedProvider.includes('flutterwave')
    ? 'flutterwave'
    : 'stripe';
  const flutterwaveTransactionId = searchParams.get('transaction_id') || searchParams.get('flw_tx_id');
  // After sandbox OTP redirect, Flutterwave sends back txRef (our reference)
  const flutterwave_txRef = searchParams.get('txRef') || searchParams.get('tx_ref');
  const maishapayTxRef = searchParams.get('transactionRefId') || searchParams.get('txRef') || searchParams.get('tx_ref') || searchParams.get('reference');
  const maishapayOperatorRef = searchParams.get('operatorRefId');
  const maishapayStatus = searchParams.get('status');
  const maishapayDescription = searchParams.get('description');
  const isApprovedProviderCallback =
    provider === 'maishapay' &&
    (maishapayStatus === '200' || (maishapayDescription || '').toUpperCase() === 'APPROVED');
  const hasMaishapayCallbackSignals = Boolean(
    maishapayTxRef ||
    maishapayOperatorRef ||
    maishapayStatus ||
    maishapayDescription ||
    window.location.href.includes('transactionRefId=') ||
    window.location.href.includes('operatorRefId=')
  );
  const isOrderSyncing = !verifying && verified && orderIds.length === 0;

  const isAsyncPendingLikeResponse = (data: any) => {
    if (!data) return false;
    if (data.pending === true) return true;
    const message = String(data.message || data.providerResponse || '').toLowerCase();
    return (
      message.includes('not completed yet') ||
      message.includes('waiting for customer confirmation') ||
      message.includes('not yet confirmed') ||
      message.includes('confirmation en cours')
    );
  };

  useEffect(() => {
    console.log('[PaymentSuccess] Component mounted');
    console.log('[PaymentSuccess] sessionId:', sessionId);
    console.log('[PaymentSuccess] provider:', provider);
    console.log('[PaymentSuccess] flutterwaveTransactionId:', flutterwaveTransactionId);
    
    if (
      !sessionId &&
      !(provider === 'flutterwave' && (flutterwaveTransactionId || flutterwave_txRef)) &&
      !(provider === 'maishapay' && (maishapayTxRef || maishapayOperatorRef)) &&
      !hasMaishapayCallbackSignals
    ) {
      console.log('[PaymentSuccess] No session ID, direct access');
      setVerifying(false);
      setVerified(true);
      return;
    }

    const doVerify = async () => {
      try {
        setVerifying(true);

        // Ensure this tab has a Supabase session by requesting it from the opener/tab in iframe
        const existing = await supabase.auth.getSession();
        if (!existing.data.session) {
          console.log('[PaymentSuccess] No local session, requesting from opener via postMessage');

          // 1) Try to get session from opener window using postMessage (most reliable when opened in new tab)
          const gotSessionViaPostMessage = await new Promise<boolean>((resolve) => {
            let settled = false;
            const timeout = setTimeout(() => {
              if (!settled) {
                settled = true;
                resolve(false);
              }
            }, 4000);

            const handler = async (event: MessageEvent) => {
              try {
                if (event.origin !== window.location.origin) return;
                const data = (event.data || {}) as any;
                if (data.type === 'SESSION_STORAGE' && data.key && data.value) {
                  try {
                    localStorage.setItem(data.key, data.value);
                  } catch (e) {
                    console.warn('[PaymentSuccess] Failed to apply SESSION_STORAGE via postMessage', e);
                  }
                }
                if (data.type === 'SESSION_DATA' && data.session?.access_token && data.session?.refresh_token) {
                  clearTimeout(timeout);
                  window.removeEventListener('message', handler);
                  try {
                    await supabase.auth.setSession({
                      access_token: data.session.access_token,
                      refresh_token: data.session.refresh_token,
                    });
                  } catch (e) {
                    console.warn('[PaymentSuccess] setSession via postMessage failed', e);
                  }
                  settled = true;
                  resolve(true);
                }
              } catch {}
            };

            window.addEventListener('message', handler);
            try {
              if (window.opener) {
                window.opener.postMessage({ type: 'REQUEST_SESSION' }, window.location.origin);
              }
            } catch (e) {
              console.warn('[PaymentSuccess] Unable to postMessage REQUEST_SESSION to opener', e);
            }
          });

          // 2) Fallback: use BroadcastChannel if postMessage didn’t work
          if (!gotSessionViaPostMessage) {
            console.log('[PaymentSuccess] Falling back to BroadcastChannel auth-sync');
            await new Promise<void>((resolve) => {
              const ch = new BroadcastChannel('auth-sync');
              const timeout = setTimeout(() => {
                console.warn('[PaymentSuccess] No session received from other tab via BroadcastChannel');
                ch.close();
                resolve();
              }, 4000);
              ch.onmessage = async (event) => {
                const data = (event.data as any);
                if (data?.type === 'SESSION_STORAGE' && data.key && data.value) {
                  try {
                    localStorage.setItem(data.key, data.value);
                    // After restoring storage, try to load session
                    const { data: s } = await supabase.auth.getSession();
                    if (s.session) {
                      clearTimeout(timeout);
                      ch.close();
                      resolve();
                      return;
                    }
                  } catch (e) {
                    console.warn('[PaymentSuccess] Failed to apply SESSION_STORAGE from BroadcastChannel', e);
                  }
                }
                if (data?.type === 'SESSION') {
                  clearTimeout(timeout);
                  const s = data.session;
                  try {
                    await supabase.auth.setSession({ access_token: s.access_token, refresh_token: s.refresh_token });
                  } catch (e) {
                    console.warn('[PaymentSuccess] setSession via BroadcastChannel failed', e);
                  }
                  ch.close();
                  resolve();
                }
              };
              ch.postMessage({ type: 'REQUEST_SESSION' });
            });
          }
        }

        console.log('[PaymentSuccess] Starting payment verification', {
          sessionId,
          provider,
          rawProvider,
          flutterwaveTransactionId,
          flutterwave_txRef,
          maishapayTxRef,
          maishapayOperatorRef,
        });

        let pendingCartFallback: { items?: unknown[]; relayId?: string; clientId?: string; createdAt?: number } | null = null;
        try {
          const pendingRaw = sessionStorage.getItem('pendingCart');
          const backupRaw = localStorage.getItem('pendingCartBackup');
          if (pendingRaw) {
            pendingCartFallback = JSON.parse(pendingRaw);
          } else if (backupRaw) {
            pendingCartFallback = JSON.parse(backupRaw);
          }
        } catch (e) {
          console.warn('[PaymentSuccess] Unable to parse pendingCart fallback payload', e);
        }

        const effectiveProvider =
          provider === 'stripe' && hasMaishapayCallbackSignals ? 'maishapay' : provider;

        const verifyFunction = effectiveProvider === 'stripe' ? 'verify-payment' : 'api-payments-verify';
        const verifyBody =
          effectiveProvider === 'flutterwave'
            ? {
                provider: 'flutterwave',
                transactionId: flutterwaveTransactionId || undefined,
                txRef: flutterwave_txRef || undefined,
              }
            : effectiveProvider === 'maishapay'
            ? {
                provider: 'maishapay',
                txRef: maishapayTxRef || undefined,
                transactionId: maishapayOperatorRef || undefined,
                cartItems: pendingCartFallback?.items,
                relayId: pendingCartFallback?.relayId,
                clientId: pendingCartFallback?.clientId,
                checkoutCreatedAt: pendingCartFallback?.createdAt,
              }
            : { sessionId };

        const { data, error } = await supabase.functions.invoke(verifyFunction, {
          body: verifyBody
        });

        console.log('[PaymentSuccess] Verification response', { data, error });

        if (error) {
          console.error('[PaymentSuccess] Verification error', error);
          // If session already processed, just show success
          if (error.message?.includes('already processed') || error.message?.includes('déjà')) {
            console.log('[PaymentSuccess] Session already processed, showing success');
            setVerified(true);
            setVerifying(false);
            return;
          }
          throw error;
        }

        if (data?.success) {
          console.log('[PaymentSuccess] Payment verified successfully', data.orderIds);
          
          // Clear cart immediately in this tab as the source of truth
          try {
            clearCart();
            console.log('[PaymentSuccess] Cart cleared successfully');
          } catch (e) {
            console.warn('[PaymentSuccess] clearCart failed, falling back to direct localStorage reset', e);
            try {
              localStorage.setItem('nazandoo-cart', '[]');
            } catch {}
          }

          // Clear pending cart from session storage
          try {
            sessionStorage.removeItem('pendingCart');
            localStorage.removeItem('pendingCartBackup');
            console.log('[PaymentSuccess] Pending cart cleared from sessionStorage');
          } catch (e) {
            console.warn('[PaymentSuccess] Failed to clear sessionStorage', e);
          }

          setVerified(true);
          setOrderIds(data.orderIds || []);
          
          // Notify other tabs/windows about successful payment
          const channel = new BroadcastChannel('payment-channel');
          channel.postMessage({ type: 'PAYMENT_SUCCESS', orderIds: data.orderIds });
          channel.close();

          // Also notify the opener tab directly (more reliable when checkout opened in a new tab)
          try {
            if (window.opener && window.location?.origin) {
              window.opener.postMessage({ type: 'PAYMENT_SUCCESS', orderIds: data.orderIds }, window.location.origin);
            }
          } catch (e) {
            console.warn('[PaymentSuccess] Unable to postMessage to opener', e);
          }
          
          toast({
            title: 'Paiement confirmé !',
            description: `Commande(s) ${data.orderIds.join(', ')} mise(s) à jour`
          });
        } else if (isAsyncPendingLikeResponse(data) || isApprovedProviderCallback) {
          console.warn('[PaymentSuccess] Payment still synchronizing', data);

          try {
            clearCart();
          } catch (e) {
            console.warn('[PaymentSuccess] clearCart failed during pending sync fallback', e);
          }

          setVerified(true);
          toast({
            title: 'Paiement approuve',
            description: 'La commande est en cours de validation. Rechargez dans quelques secondes.',
          });
        } else {
          console.warn('[PaymentSuccess] Payment not completed', data);
          toast({
            title: 'Erreur de vérification',
            description: 'Impossible de vérifier le paiement',
            variant: 'destructive'
          });
        }
      } catch (error: any) {
        console.error('[PaymentSuccess] Exception during verification', error);

        const effectiveProviderForFallback =
          provider === 'stripe' && hasMaishapayCallbackSignals ? 'maishapay' : provider;
        const isApprovedMaishapayCallback =
          effectiveProviderForFallback === 'maishapay' &&
          (maishapayStatus === '200' || (maishapayDescription || '').toUpperCase() === 'APPROVED');

        if (isApprovedMaishapayCallback) {
          // Payment provider already approved the transaction in callback URL.
          // Clear visible cart but keep pendingCartBackup so verification can be retried.
          try {
            clearCart();
          } catch (e) {
            console.warn('[PaymentSuccess] clearCart failed in approved fallback', e);
          }

          setVerified(true);
          setVerifying(false);
          toast({
            title: 'Paiement approuve',
            description: 'Le panier est vide. Validation de la commande en cours, rechargez dans quelques secondes.',
          });
          return;
        }

        toast({
          title: 'Erreur',
          description: error.message,
          variant: 'destructive'
        });
      } finally {
        console.log('[PaymentSuccess] Verification complete');
        setVerifying(false);
      }
    };

    doVerify();
  }, [
    sessionId,
    provider,
    rawProvider,
    flutterwaveTransactionId,
    flutterwave_txRef,
    maishapayTxRef,
    maishapayOperatorRef,
    maishapayStatus,
    maishapayDescription,
    hasMaishapayCallbackSignals,
    toast,
  ]);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container py-8">
        <div className="max-w-2xl mx-auto">
          <Card className="text-center">
            <CardHeader className="pb-6">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-success rounded-full flex items-center justify-center">
                  <CheckCircle className="h-8 w-8 text-success-foreground" />
                </div>
              </div>
              <CardTitle className="text-2xl text-success">
                {verifying
                  ? "Vérification du paiement..."
                  : isOrderSyncing
                  ? "Paiement approuvé"
                  : "Paiement réussi !"}
              </CardTitle>
              <CardDescription className="text-lg">
                {verifying 
                  ? "Nous vérifions votre paiement, veuillez patienter..."
                  : isOrderSyncing
                    ? "Paiement validé. Nous finalisons votre commande, cela prend quelques secondes."
                  : verified 
                    ? "Votre commande a été confirmée avec succès"
                    : "Merci pour votre commande !"
                }
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-6">
              {isOrderSyncing && (
                <div className="space-y-4">
                  <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-left">
                    <p className="text-sm font-medium text-amber-900 flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Validation de commande en cours
                    </p>
                    <p className="text-sm text-amber-800 mt-1">
                      Si la commande n'apparait pas tout de suite, rechargez cette page dans 5 a 10 secondes.
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3 pt-1">
                    <Button className="flex-1" onClick={() => window.location.reload()}>
                      Recharger la page
                    </Button>
                    <Button variant="outline" className="flex-1" onClick={() => navigate('/dashboard')}>
                      Voir mes commandes
                    </Button>
                  </div>
                </div>
              )}

              {!verifying && verified && orderIds.length > 0 && (
                <>
                  {/* Order Status Steps */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                    <div className="flex flex-col items-center space-y-2 p-4 bg-success/10 rounded-lg">
                      <CheckCircle className="h-6 w-6 text-success" />
                      <span className="text-sm font-medium text-success">Paiement confirmé</span>
                    </div>
                    <div className="flex flex-col items-center space-y-2 p-4 bg-muted rounded-lg">
                      <Package className="h-6 w-6 text-muted-foreground" />
                      <span className="text-sm font-medium">Préparation</span>
                    </div>
                    <div className="flex flex-col items-center space-y-2 p-4 bg-muted rounded-lg">
                      <MapPin className="h-6 w-6 text-muted-foreground" />
                      <span className="text-sm font-medium">Prêt au retrait</span>
                    </div>
                  </div>

                  {/* Pickup Token Display */}
                  {orderIds.map((orderId) => (
                    <PickupTokenDisplay key={orderId} orderId={orderId} />
                  ))}

                  {/* Actions */}
                  <div className="flex flex-col sm:flex-row gap-3 pt-4">
                    <Button 
                      className="flex-1" 
                      onClick={() => navigate('/dashboard')}
                    >
                      Voir mes commandes
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => navigate('/products')}
                    >
                      Continuer mes achats
                    </Button>
                  </div>

                  <p className="text-sm text-muted-foreground">
                    Besoin d'aide ? <Button variant="link" className="p-0 h-auto">Contactez notre support</Button>
                  </p>
                </>
              )}
              
              {verifying && (
                <div className="flex justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PaymentSuccess;